import React, { useContext } from "react";
import Container from "@src/components/Container";
import { Button, Space, theme, BodyText } from "@src/components/nessie-web";
import { TranslationContext } from "@src/components/translation/TranslationContext";
import { ModalContext, ModalType } from "@src/components/modals/ModalController";
import styled from "@emotion/styled";
import { mediaQueries } from "@src/styles/theme";
import { logEvent } from "@src/utils/logClient";

const {
  colors: { dt_white, dt_taro90 },
  radii: { dt_radius_m },
} = theme;

const CTASectionContainer = styled.section<{ bgImage: string }>`
  background-image: url("${(props) => props.bgImage}");
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center top;
`;

const CTAContent = styled.div`
  width: 100%;
  min-height: 520px;
  display: flex;
  justify-content: center;
  align-items: center;

  ${mediaQueries[0]} {
    min-height: 640px;
  }
`;

const CTATile = styled.div`
  text-align: center;
  width: 100%;
  max-width: 560px;
  padding: 30px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: ${dt_white};
  border-radius: ${dt_radius_m};
  margin-top: 54px;

  ${mediaQueries[0]} {
    padding: 54px;
    margin-top: 108px;
  }
`;

const CTATitle = styled.h2`
  font-weight: 800;
  font-size: 30px;
  line-height: 36px;
  text-align: center;
  letter-spacing: -0.35px;
  color: ${dt_taro90};

  ${mediaQueries[0]} {
    font-weight: 800;
    font-size: 50px;
    line-height: 100%;
    text-align: center;
    letter-spacing: -0.5px;
  }
`;

type CTASectionProps = {
  cta_title: string;
  cta_button_text: string;
  cta_background_image_url: string;
  cta_advice?: string;
  cta_button_url?: string;
  button_log_event_name?: string;
  params?: any;
};

const CTASection: React.FC<CTASectionProps> = ({
  cta_title,
  cta_button_text,
  cta_background_image_url,
  cta_advice,
  cta_button_url,
  button_log_event_name,
  params,
}) => {
  const t = useContext(TranslationContext);

  const modalContext = useContext(ModalContext);

  function fireLogEvent() {
    logEvent({
      eventName: button_log_event_name,
      eventValue: location.href,
      metadata: params,
    });
  }

  function openSchoolLeaderModal() {
    modalContext.showModal(ModalType.SchoolLeaderSignup);
    fireLogEvent();
  }

  return (
    <CTASectionContainer bgImage={cta_background_image_url}>
      <Container>
        <CTAContent>
          <CTATile>
            <CTATitle>{t.translate(cta_title)}</CTATitle>
            <Space size="m" />
            {cta_button_url ? (
              <Button href={cta_button_url} onClick={fireLogEvent}>
                {t.translate(cta_button_text)}
              </Button>
            ) : (
              <Button onClick={openSchoolLeaderModal}>{t.translate(cta_button_text)}</Button>
            )}
            <Space size="m" />
            {cta_advice && <BodyText>{t.translate(cta_advice)}</BodyText>}
          </CTATile>
        </CTAContent>
      </Container>
    </CTASectionContainer>
  );
};

export default CTASection;
